import { Event, EventListener, Animation, onDocumentReady } from "silver";

interface WindowLayoutEvent extends Event {
	layout: WindowLayout;
}

export function getViewportSize(windowObject: Window = window): { width: number, height: number } {
	const documentElement = windowObject.document.documentElement;
	return {
		width:  Math.max(documentElement.clientWidth, windowObject.innerWidth || 0),
		height: Math.max(documentElement.clientHeight, windowObject.innerHeight || 0)
	};
}

export class WindowLayout {

	private scrollXValue: number = 0;
	private scrollYValue: number = 0;
	private documentWidthValue: number = 0;
	private documentHeightValue: number = 0;
	private viewportWidthValue: number = 0;
	private viewportHeightValue: number = 0;

	private scrollAnimatingValue: boolean = false;
	private lastAnimation: Animation|null = null;

	public readonly onScroll = EventListener.create<WindowLayoutEvent>("scroll");
	public readonly onResize = EventListener.create<WindowLayoutEvent>("resize");
	public readonly onReady  = EventListener.create<WindowLayoutEvent>("ready");
	public readonly onUpdate = EventListener.create<WindowLayoutEvent>("update");

	protected static cancelingEvents: string[] = ["mousewheel", "DOMMouseScroll", "keydown"];

	constructor(private windowObject: Window = window) {

		this.windowObject.addEventListener("scroll", () => { this.handleScrollEvent(); });
		this.windowObject.addEventListener("resize", () => { this.handleResizeEvent(); });

		onDocumentReady(() => this.doReady());
		this.updateSize();

		setInterval(() => { this.checkDocumentResize(); }, 300);
	}

	public get scrollX(): number {
		return this.scrollXValue;
	}

	public get scrollY(): number {
		return this.scrollYValue;
	}

	public get documentWidth(): number {
		return this.documentWidthValue;
	}

	public get documentHeight(): number {
		return this.documentHeightValue;
	}

	public get viewportWidth(): number {
		return this.viewportWidthValue;
	}

	public get viewportHeight(): number {
		return this.viewportHeightValue;
	}

	public get scrollAnimating(): boolean {
		return this.scrollAnimatingValue;
	}

	private handleResize(): void {
		this.onResize.trigger({ layout: this });
		this.onUpdate.trigger({ layout: this });
	}

	private updateDocumentSize(): void {
		const { body, documentElement: html } = this.windowObject.document;
		this.documentWidthValue = Math.max(body.scrollWidth, body.offsetWidth, html.clientWidth, html.scrollWidth, html.offsetWidth);
		this.documentHeightValue = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
	}

	private updateScroll(): void {
		const html = this.windowObject.document.documentElement;

		this.scrollXValue = (this.windowObject.pageXOffset || html.scrollLeft) - (html.clientLeft || 0);
		this.scrollYValue = (this.windowObject.pageYOffset || html.scrollTop) - (html.clientTop || 0);
	}

	private updateSize(): void {
		this.updateScroll();

		const size = getViewportSize(this.windowObject);

		this.viewportWidthValue = size.width;
		this.viewportHeightValue = size.height;

		this.updateDocumentSize();
	}

	private handleScrollEvent(): void {
		this.updateScroll();
		this.onScroll.trigger({ layout: this });
	}

	private checkDocumentResize(): void {
		const lastHeight = this.documentHeightValue;
		const lastWidth = this.documentWidthValue;

		this.updateSize();

		if (lastHeight == this.documentHeightValue && lastWidth == this.documentWidthValue) {
			return;
		}
		this.handleResize();
	}

	private handleResizeEvent(): void {
		this.updateSize();
		this.handleResize();
	}

	private doReady(): void {
		this.updateSize();
		this.onReady.trigger({ layout: this });
		this.onResize.trigger({ layout: this });
		this.onUpdate.trigger({ layout: this });
	}

	public doUpdate(): void {
		this.updateSize();
		this.onUpdate.trigger({ layout: this });
	}

	public scrollIntoView(element: HTMLElement, duration?: number, force: boolean = false): Promise<void> {
		const { left, top, height, width } = element.getBoundingClientRect();

		return this.scrollToBox({
			left: left + this.scrollX,
			top:  top + this.scrollY,
			width,
			height
		}, duration, force);
	}

	public scrollToElement(element: HTMLElement, duration?: number, force: boolean = false): Promise<void> {
		const pos = element.getBoundingClientRect();
		const scrollX = window.scrollX + pos.left;
		const scrollY = window.scrollY + pos.top;

		return this.scrollTo(scrollX, scrollY, duration, force);
	}

	public scrollToBox(box: Record<"left"|"top"|"width"|"height", number>, duration?: number, force: boolean = false): Promise<void> {
		let targetX = this.scrollX;
		let targetY = this.scrollY;
		if (box.top < this.scrollY) {
			targetY = box.top;
		} else if (box.top + box.height > this.scrollY + this.viewportHeight) {
			targetY = box.top + box.height - this.viewportHeight;
		}
		if (box.left < this.scrollX) {
			targetX = box.left;
		} else if (box.left + box.width > this.scrollX + this.viewportWidth) {
			targetX = box.left + box.width - this.viewportWidth;
		}
		return this.scrollTo(targetX, targetY, duration, force);
	}

	public scrollTo(x: number, y: number, duration: number = 500, force: boolean = false): Promise<void> {
		if (x == this.scrollXValue && y === this.scrollYValue) {
			return Promise.resolve();
		}
		this.updateDocumentSize();

		y = Math.min(y, this.documentHeightValue - this.viewportHeightValue);

		const fromY = this.scrollY;
		const fromX = this.scrollX;
		const distanceY = (y - fromY);
		const distanceX = (x - fromX);

		if (this.lastAnimation) {
			this.lastAnimation.stop();
		}

		this.scrollAnimatingValue = true;

		return new Promise((resolve, reject) => {
			const unbinds: Array<(...args: any[]) => any> = [];
			const cleanup = () => {
				this.scrollAnimatingValue = false;
				this.lastAnimation = null;
				unbinds.forEach(unbind => unbind());
			};
			if (!force) {
				const onScroll = (): void => {
					animation.stop();
					reject("manual scrolling");
					cleanup();
				};
				WindowLayout.cancelingEvents.forEach((eventName) => {
					this.windowObject.addEventListener(eventName, onScroll);
					unbinds.push(() => {
						this.windowObject.removeEventListener(eventName, onScroll);
					});
				});
			}

			const animation = new Animation(({ time, progress }) => {
				this.windowObject.scrollTo(
					(time * distanceX) + fromX,
					(time * distanceY) + fromY
				);
				if (progress >= 1) {
					resolve();
					cleanup();
				}
			}, duration, Animation.Easing.easeInOutCubic);

			animation.setMaxStep(30);
			animation.start();

			this.lastAnimation = animation;
		});
	}
}
